/**
 * revealAndOpenTask — navigate to a task wherever it lives.
 *
 * Given only a task ID, this function:
 * 1. Loads the task from the database
 * 2. Determines which view/container it belongs to
 * 3. Navigates to that view with ?tid= to open the task detail
 *
 * Used by:
 * - QuickFind modal (search result selection)
 * - Push notifications / deep links (totodo://task/:id)
 * - Reminder taps
 * - Shared links
 *
 * The full 6-step algorithm from the master plan (§32) for auto-expanding
 * hidden sections (Show More, Show Later, Show Logged) requires view state
 * access and will be handled by the receiving ViewShell via useEffect on
 * the ?tid= parameter. This function handles step 6: "task not in current
 * view → navigate to the correct view".
 */

import { db, ObjectType, ObjectStatus, StartAtType } from "@/db/schema"
import { parseDbDate } from "@totodo/operations"
import { ROUTES } from "@/config/routes"

/**
 * Determine the best route to view a specific task.
 *
 * Resolution order:
 * 1. Deleted task → /trash?tid=id
 * 2. Task in container → /project|list|area/:containerId?tid=id
 * 3. Task in inbox → /inbox?tid=id
 * 4. Task is completed/canceled → container route if has parent, else /logbook?tid=id
 * 5. Task with startAt → Today/Upcoming/Someday/Anyday based on scheduling
 * 6. Fallback → /today?tid=id
 */
export async function resolveTaskRoute(taskId: string): Promise<string | null> {
  const task = await db.totodo.get(taskId)
  if (!task) return null // Task doesn't exist

  // Checklist items and subtasks (type < 0) are not visible in view lists.
  // Navigate to the parent task instead, which IS visible.
  if (task.type < 0) {
    const segments = task.parentPath.split("/")
    // parentPath = "root/PROJECT_ID/PARENT_TASK_ID" → last segment is the parent task
    const parentTaskId = segments[segments.length - 1]
    if (parentTaskId && !parentTaskId.startsWith("#")) {
      return resolveTaskRoute(parentTaskId)
    }
    // Fallback: can't resolve parent, skip
    return null
  }

  const tid = `?tid=${taskId}`

  // Deleted → Trash
  if (task.isDeleted === 1) {
    return `${ROUTES.TRASH}${tid}`
  }

  // Parse parentPath
  const segments = task.parentPath.split("/")
  const parentKey = segments.length >= 2 ? segments[1] : null

  // Completed/Canceled task
  if (
    task.status === ObjectStatus.Completed ||
    task.status === ObjectStatus.Canceled
  ) {
    // If in a container, navigate there (container view has logged section)
    if (parentKey && !parentKey.startsWith("#")) {
      const parent = await db.totodo.get(parentKey)
      if (parent) {
        return `${containerRoute(parent)}${tid}`
      }
    }
    // Fallback to logbook
    return `${ROUTES.LOGBOOK}${tid}`
  }

  // Task in a container (project/list/area)
  if (parentKey && !parentKey.startsWith("#")) {
    const parent = await db.totodo.get(parentKey)
    if (parent) {
      return `${containerRoute(parent)}${tid}`
    }
  }

  // Inbox
  if (parentKey === "#inbox") {
    return `${ROUTES.INBOX}${tid}`
  }

  // Scheduled tasks — check startAtIndex
  const startAtEntry = await db.startAtIndex.get(`#${taskId}`)
  if (startAtEntry) {
    if (startAtEntry.type === StartAtType.Someday) {
      return `${ROUTES.SOMEDAY}${tid}`
    }
    // Default type with startAt → Today or Upcoming
    if (startAtEntry.startAt) {
      const now = Date.now()
      const startTime = parseDbDate(startAtEntry.startAt) ?? 0
      if (startTime > now) {
        return `${ROUTES.UPCOMING}${tid}`
      }
      return `${ROUTES.TODAY}${tid}`
    }
  }

  // Anyday (root/#anyday) or fallback
  return `${ROUTES.TODAY}${tid}`
}

/** Get the route path for a container object. */
function containerRoute(container: { id?: string; type: ObjectType }): string {
  const id = container.id as string
  switch (container.type) {
    case ObjectType.Project:
      return ROUTES.PROJECT(id)
    case ObjectType.List:
      return ROUTES.LIST(id)
    case ObjectType.Area:
      return ROUTES.AREA(id)
    default:
      return ROUTES.TODAY
  }
}

/**
 * React hook version — navigates to a task and opens its detail panel.
 *
 * Usage:
 * ```tsx
 * const goToTask = useRevealTask()
 * goToTask("task-id-123")
 * ```
 */
export { useRevealTask } from "./useRevealTask"
